import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { ImageCapture } from "../components/ImageCapture";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Trash2, Copy, Save, X, Facebook, ClipboardList, Lock, ImageOff } from "lucide-react";

const BE = process.env.REACT_APP_BACKEND_URL;

function CopyBtn({ text, testid }) {
  return (
    <button onClick={() => { navigator.clipboard.writeText(text || ""); toast.success("Copied"); }}
      className="pm-btn-ghost text-xs flex items-center gap-1.5" data-testid={testid}>
      <Copy className="w-3.5 h-3.5" /> Copy
    </button>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [it, setIt] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [addingPhoto, setAddingPhoto] = useState(false);
  const [photoKey, setPhotoKey] = useState(0);
  const [packing, setPacking] = useState(null);

  const load = () => api.get(`/listings/${id}`).then((r) => {
    setIt(r.data);
    setForm({ kind: r.data.kind, title: r.data.title, price: r.data.price ?? "", category: r.data.category || "", condition: r.data.condition || "Good", description: r.data.description || "" });
  }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, [id]);

  if (!it || !form) return <Layout><div className="label-mono" data-testid="listing-loading">Loading…</div></Layout>;

  const fld = (k) => ({ value: form[k], onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })) });

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, price: form.price === "" ? null : Number(form.price) };
      if (form.kind === "service") body.condition = null;
      await api.put(`/listings/${id}`, body);
      toast.success("Saved"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addPhoto = async (b64, dataUrl, ext) => {
    if (!b64) return;
    setAddingPhoto(true);
    try {
      await api.post(`/listings/${id}/photos`, { image_base64: b64, ext: ext || "jpg" });
      toast.success("Photo added"); setPhotoKey((k) => k + 1); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAddingPhoto(false); }
  };

  const delPhoto = async (pid) => {
    try { await api.delete(`/listings/${id}/photos/${pid}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try { await api.post(`/listings/${id}/analyze`); toast.success("AI review ready"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const makePacket = async (channel) => {
    setPacking(channel);
    try { await api.post(`/listings/${id}/packet`, { channel }); toast.success("Packet ready"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPacking(null); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this listing and its photos from your account? This cannot be undone.")) return;
    try { await api.delete(`/listings/${id}`); toast.success("Listing removed"); nav("/listings"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const fb = it.aiFeedback;
  const channels = it.channels || {};

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl" data-testid="listing-detail-page">
        <button onClick={() => nav("/listings")} className="pm-btn-ghost text-sm flex items-center gap-2" data-testid="listing-back"><ArrowLeft className="w-4 h-4" /> All listings</button>

        {/* Edit form */}
        <div className="pm-card p-5 space-y-3" data-testid="listing-edit-form">
          <div className="flex gap-2">
            {["product", "service"].map((k) => (
              <button key={k} onClick={() => setForm((f) => ({ ...f, kind: k }))} className="badge"
                style={{ background: form.kind === k ? "var(--text)" : "transparent", color: form.kind === k ? "#fff" : "var(--text)" }}>{k}</button>
            ))}
          </div>
          <input className="pm-input" {...fld("title")} data-testid="edit-title" />
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="pm-input" type="number" placeholder="Price (USD)" {...fld("price")} data-testid="edit-price" />
            <input className="pm-input" placeholder="Category" {...fld("category")} data-testid="edit-category" />
          </div>
          {form.kind === "product" && (
            <select className="pm-input" {...fld("condition")} data-testid="edit-condition">
              {["New", "Like New", "Good", "Fair", "For parts"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <textarea className="pm-input" rows={4} placeholder="Description" {...fld("description")} data-testid="edit-description" />
          <div className="flex gap-2 flex-wrap">
            <button onClick={save} disabled={saving} className="pm-btn text-sm flex items-center gap-2" data-testid="listing-save"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</button>
            <button onClick={remove} className="pm-btn-ghost text-sm flex items-center gap-2 ml-auto" data-testid="listing-delete"><Trash2 className="w-4 h-4" /> Remove listing</button>
          </div>
        </div>

        {/* Photos */}
        <div className="pm-card p-5 space-y-3" data-testid="listing-photos">
          <div className="label-mono">Photos</div>
          {it.photos?.length > 0 ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {it.photos.map((p) => (
                <div key={p.id} className="relative group" data-testid={`photo-${p.id}`}>
                  <img src={`${BE}${p.url}`} alt="listing" className="w-full h-24 object-cover rounded" />
                  <button onClick={() => delPhoto(p.id)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1" data-testid={`photo-delete-${p.id}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          ) : <p className="text-sm flex items-center gap-2" style={{ color: "var(--text-2)" }}><ImageOff className="w-4 h-4" /> No photos yet.</p>}
          <ImageCapture key={photoKey} onImage={(b, d) => addPhoto(b, d, "jpg")} />
          {addingPhoto && <p className="label-mono">Uploading photo…</p>}
        </div>

        {/* AI Improve */}
        <div className="pm-card p-5 space-y-3" data-testid="listing-ai">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="label-mono flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI: improve this listing</div>
            <button onClick={analyze} disabled={analyzing} className="pm-btn text-sm flex items-center gap-2" data-testid="listing-analyze-btn">
              <Sparkles className="w-4 h-4" /> {analyzing ? "Analyzing…" : (fb ? "Re-analyze" : "Improve this listing")}
            </button>
          </div>
          {fb && (
            <div className="space-y-4 text-sm" data-testid="listing-ai-feedback">
              {fb.titleSuggestions?.length > 0 && (
                <div>
                  <div className="label-mono mb-1">Stronger titles (tap to use)</div>
                  <div className="space-y-1">
                    {fb.titleSuggestions.map((t, i) => (
                      <button key={i} onClick={() => setForm((f) => ({ ...f, title: t }))} className="pm-card w-full text-left p-2 text-sm" data-testid={`ai-title-${i}`}>{t}</button>
                    ))}
                  </div>
                </div>
              )}
              {fb.improvedDescription && (
                <div>
                  <div className="label-mono mb-1 flex items-center justify-between">Improved description
                    <span className="flex gap-2">
                      <button onClick={() => setForm((f) => ({ ...f, description: fb.improvedDescription }))} className="pm-btn-ghost text-xs" data-testid="ai-use-description">Use this</button>
                      <CopyBtn text={fb.improvedDescription} testid="ai-copy-description" />
                    </span>
                  </div>
                  <p style={{ color: "var(--text-2)" }}>{fb.improvedDescription}</p>
                </div>
              )}
              {fb.suggestedCategory && <div><span className="label-mono">Category:</span> {fb.suggestedCategory}</div>}
              {fb.priceFeedback && <div><span className="label-mono">Price:</span> {fb.priceFeedback}</div>}
              {fb.photoFeedback?.length > 0 && (
                <div><div className="label-mono mb-1">Photo feedback</div><ul className="list-disc pl-5 space-y-1" style={{ color: "var(--text-2)" }}>{fb.photoFeedback.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
              )}
              {fb.sellingTips?.length > 0 && (
                <div><div className="label-mono mb-1">Selling tips</div><ul className="list-disc pl-5 space-y-1" style={{ color: "var(--text-2)" }}>{fb.sellingTips.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
              )}
            </div>
          )}
        </div>

        {/* Publish to channels */}
        <div className="pm-card p-5 space-y-4" data-testid="listing-publish">
          <div className="label-mono">Post to marketplaces</div>

          {[{ key: "facebook", label: "Facebook Marketplace", icon: Facebook }, { key: "craigslist", label: "Craigslist", icon: ClipboardList }].map(({ key, label, icon: Icon }) => {
            const ch = channels[key] || {};
            const packet = ch.packet;
            return (
              <div key={key} className="border p-3 rounded" style={{ borderColor: "var(--border-subtle)" }} data-testid={`channel-${key}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-semibold text-sm flex items-center gap-2"><Icon className="w-4 h-4" /> {label}</div>
                  <button onClick={() => makePacket(key)} disabled={packing === key} className="pm-btn text-xs" data-testid={`packet-btn-${key}`}>
                    {packing === key ? "Building…" : (packet ? "Regenerate packet" : "Generate copy-paste packet")}
                  </button>
                </div>
                {packet && (
                  <div className="mt-3 space-y-2 text-sm" data-testid={`packet-${key}`}>
                    <div className="flex items-center justify-between"><span className="label-mono">Title</span><CopyBtn text={packet.title} testid={`copy-title-${key}`} /></div>
                    <div style={{ color: "var(--text-2)" }}>{packet.title}</div>
                    <div className="label-mono">Price: {packet.price} · Category: {packet.category}</div>
                    <div className="flex items-center justify-between"><span className="label-mono">Body</span><CopyBtn text={packet.body} testid={`copy-body-${key}`} /></div>
                    <p className="whitespace-pre-wrap" style={{ color: "var(--text-2)" }}>{packet.body}</p>
                    {packet.tips?.length > 0 && <ul className="list-disc pl-5" style={{ color: "var(--text-2)" }}>{packet.tips.map((t, i) => <li key={i}>{t}</li>)}</ul>}
                    <p className="label-mono">Paste this into {label} and attach your photos.</p>
                  </div>
                )}
              </div>
            );
          })}

          {["eBay"].map((name) => (
            <div key={name} className="border p-3 rounded flex items-center justify-between gap-2" style={{ borderColor: "var(--border-subtle)", opacity: 0.7 }} data-testid={`channel-${name.toLowerCase()}`}>
              <div className="font-semibold text-sm">{name}</div>
              <span className="badge flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Connect coming soon</span>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
